import { AsyncPipe, DOCUMENT, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterContentInit,
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  Directive,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { NgbActiveModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { addBreadcrumb, SeverityLevel } from '@sentry/angular';
import { merge, Observable, of } from 'rxjs';
import { take } from 'rxjs/operators';
import { ButtonComponent, GroupButtonComponent } from '../button';
import { DOMEvent } from '../form/input-base/input-base.model';
import { InputButton } from '../form/input-base/input-pre-append/input-button';
import { LoaderComponent } from '../loader/loader.component';
import { ModalService, ModalSettings } from '../modal/modal.service';
import { BootstrapTheme } from '../utils';
import { WizardStepMarks } from './utils/marks';
import { WizardStepQueue } from './utils/queue';
import { WizardStepComponent } from './wizard-step.component';
import { WizardTitle } from './wizard-title.directive';
import { WizardStep } from './WizardStep';

const WIZARD_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => WizardComponent),
  multi: true
};

const discardChangesText = 'Discard Changes';

@Directive({
  selector: '[mbsWizardFooterPrepend]'
})
export class WizardFooterPrependDirective {
  template = inject<TemplateRef<any>>(TemplateRef);

  @Input() hide = false;
}

@UntilDestroy()
@Component({
  selector: 'mbs-wizard',
  templateUrl: './wizard.component.html',
  styleUrls: ['./wizard.component.scss'],
  host: {
    '(window:keydown.esc)': 'closeWizard($event)',
    '(document:keyup.enter)': 'nextStepWizard($event)',
    '(document:keyup.backspace)': 'backStepWizard($event)'
  },
  providers: [WIZARD_VALUE_ACCESSOR],
  imports: [AsyncPipe, ButtonComponent, GroupButtonComponent, LoaderComponent, NgClass, NgbTooltip, NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WizardComponent implements OnInit, OnDestroy, OnChanges, AfterContentInit, AfterViewChecked, ControlValueAccessor {
  activeModal = inject(NgbActiveModal);
  private cd = inject(ChangeDetectorRef);
  private modalService = inject(ModalService);
  private sanitizer = inject(DomSanitizer);
  private document = inject<Document>(DOCUMENT);

  public readonly defaultWizardHeight = '70vh';
  public readonly defaultWizardMinHeight = '600px';

  @Input() ignoreSteps: string[] = [];
  @Input() height: string | number = this.defaultWizardHeight;
  @Input() loading = false;
  @Input() buttonLoadingMode = false;
  @Input() stepQueue: WizardStepQueue = null;
  @Input() stepMarks: WizardStepMarks = new WizardStepMarks();
  @Input() nextAndBackButtonType: BootstrapTheme = 'primary';
  @Input() hideNextAndBackButtons? = false;
  @Input() backButton: InputButton = { text: 'Back' };
  @Input() nextButton: InputButton = { text: 'Next' };
  @Input() saveButton: InputButton = { text: 'Save' };
  @Input() saveButtonGroup?: InputButton[];
  @Input() cancelButton: InputButton = { text: 'Cancel' };
  @Input() disableSpinner = false;
  @Input() hideCrossButton? = false;
  @Input() showSaveBtnOnClose = false;
  @Input() scrollableStepContent = false;
  @Input() firstStepTitle: string;
  @Input() cover: string;

  /**
   * `True` - wizard will close modal
   * @param {boolean} value
   */
  @Input() set canClosed(value: boolean) {
    if (value) {
      setTimeout(() => {
        this.spinnerShow = false;
        this.closeModal('Save');
      }, 100);
    }
  }

  /**
   * If `false` wizard will not display whole footer.
   */
  @Input() showFooter = true;

  /**
   * If `false` wizard will not check changes by itself, when it closes.
   */
  @Input() checkChanges = true;

  /**
   * Wizard title
   */
  @Input() title = 'Wizard Title';
  /**
   * Steps orientation.\
   * if value is not set, the steps location will be based on the number of steps.\
   * 5 > is `vertical` else `horizontal`
   */
  @Input() stepsOrientation: 'horizontal' | 'vertical';
  /**
   * Set false to hide steps.
   */
  @Input() showSteps = true;
  /**
   * Custom complete style step
   */
  @Input() completeClass = 'success';
  @Input() invalidClass = 'invalid';
  /**
   * Custom active style step
   */
  @Input() activeClass = 'active';

  @Input() isOpenSteps = false;

  @Input() stepsContainerClass = '';

  @Input() blockEnterIfControlFocus = true;

  /**
   * Disable internal @HostListener if the wizard has complex logic
   * Next step action on Enter key will be disabled
   * Previous step action on Backspace will be disabled
   */
  @Input() disableStepChangeOnKeyboardKey = false;

  /**
   * Pass 'true' to prevent 'Unsaved Changes' modal showing
   */
  @Input() canCloseWithoutSave = false;

  /**
   * Custom handler, which called on close button click
   */
  @Input() closeHandler?: () => Observable<any>;

  /**
   * Pass 'true' in case of initial data loading.
   * Difference between this prop and 'loading' in a loader-content position
   * In case of 'initialLoading' loader occupies all content area. Step tabs doesn't display
   */
  @Input() initialLoading = false;

  /**
   * Emit on finish save click. Wizard don't close self after the save emit.\
   * You need to set `canClosed` flag to close it.
   */
  @Output('save') saveEvent = new EventEmitter<any>();
  @Output('next') nextEvent = new EventEmitter<WizardStep>();
  @Output() closeModalEvent = new EventEmitter<string>();

  @Output() stepSelected = new EventEmitter<WizardStep>();

  @Output() stepScroll = new EventEmitter();

  @ViewChild('header', { static: true }) header: ElementRef<HTMLDivElement>;
  @ViewChild('footer', { static: false }) footer: ElementRef<HTMLDivElement>;

  /**
   * Find all available steps @see WizardStepComponent
   */
  @ContentChildren(WizardStepComponent) steps: QueryList<WizardStepComponent>;
  @ContentChild(WizardTitle, { static: true, read: WizardTitle }) wizardTitle: WizardTitle;
  @ContentChild(WizardFooterPrependDirective, { static: false, read: WizardFooterPrependDirective }) prepend: WizardFooterPrependDirective;

  public sanitizedHeight: SafeStyle = this.defaultWizardHeight;
  public sanitizedMinHeight: SafeStyle = this.defaultWizardMinHeight;

  public readonly unsavedChangesSettings: ModalSettings = {
    header: { title: 'Unsaved Changes' },
    footer: { okButton: { text: 'Back' }, cancelButton: { text: discardChangesText } }
  };

  public spinnerShow = false;

  private internalStep: WizardStepComponent;
  private confirmClose: boolean;

  set currentStep(value: WizardStepComponent) {
    this.nextEvent.emit(value);
    this.internalStep.nextEvent.emit(this.internalStep);
    this.updateStepState(value);
    this.internalStep = value;
    this.notifyValueChange();
    this.stepQueue?.registerStepDisplay(value.title);
  }

  get currentStep(): WizardStepComponent {
    return this.internalStep;
  }

  get allStepValid(): boolean {
    return !this.steps?.some((s) => s.valid === false);
  }

  get saveButtonDisabled(): boolean {
    return (
      this.saveButton.loading ||
      this.saveButton.disabled ||
      (!this.disableSpinner && this.spinnerShow) ||
      (this.buttonLoadingMode && this.loading) ||
      this.stepMarks?.needBlock('save') ||
      (!this.isOpenSteps && this.steps?.last !== this.currentStep && !this.disableSpinner)
    );
  }

  get saveButtonTooltip(): string {
    return (this.saveButtonDisabled && this.stepMarks.getMark(this.currentStep.title, 'save').description) || null;
  }

  ngAfterViewChecked(): void {
    if (this.height && !this.sanitizedHeight) this.setSanitizedHeight();
  }

  onChange: (value) => void;
  onTouched: () => void;

  handleSave(action?: () => void): void {
    const hasQueue = this.stepQueue?.getQueue().length;
    const allStepValid = this.allStepValid;

    if (allStepValid && !hasQueue) {
      this.spinnerShow = true;
      return void (action ? action() : this.saveEvent.emit());
    }

    this.currentStep.isSave = true;
    allStepValid ? this.goToRequiredStep(true) : this.goToErrorStep(true);

    if (!this.currentStep.valid) {
      this.currentStep.nextEvent.emit(this.currentStep);
    }
  }

  handleClose(): void {
    this.runCloseStrategy();
  }

  closeWizard(event: KeyboardEvent): void {
    const modalList = this.document.querySelectorAll('ngb-modal-window');
    const listLength = modalList.length;
    const wizardIsNotTopModal = listLength > 1 && !modalList[listLength - 1].querySelector('mbs-wizard');

    if (wizardIsNotTopModal) return;

    event.preventDefault();
    event.stopPropagation();

    const force = !this.currentStep.hasChanges && !this.steps.some((s) => s.changesBeforeValidation);
    const reason = 'keyboard:escape';

    this.runCloseStrategy(force, reason);
  }

  closeWithConfirm(reason: string): void {
    this.runCloseStrategy(false, reason);
  }

  nextStepWizard(event: KeyboardEvent & DOMEvent<HTMLInputElement>): void {
    if (this.disableStepChangeOnKeyboardKey) return;
    if (
      !this.loading &&
      !this.spinnerShow &&
      (!this.blockEnterIfControlFocus ||
        !this.currentStep.blockEnterIfControlFocus ||
        (event.target && (event.target.classList.contains('mbs-wizard_nav-tabs') || event.target.type == null)))
    ) {
      this.steps.last === this.currentStep ? this.handleSave() : this.changeStep(1);
    }
  }

  backStepWizard(event: KeyboardEvent & DOMEvent<HTMLInputElement>): void {
    if (this.disableStepChangeOnKeyboardKey) return;

    if (!this.loading && event?.target?.type == null && this.steps.first !== this.currentStep) this.changeStep(-1);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.steps && changes && changes.isOpenSteps && changes.isOpenSteps.currentValue) {
      this.steps.forEach((step) => (step.completed = true));
    }

    if (changes?.height) {
      this.setSanitizedHeight();
    }

    if (changes?.firstStepTitle && this.steps?.length) {
      this.firstStepTitle && this.setFirstStep(this.steps.toArray());
    }
  }

  ngAfterContentInit(): void {
    if (this.isOpenSteps) this.steps.forEach((step) => (step.completed = true));

    this.steps.changes.pipe(untilDestroyed(this)).subscribe((v: WizardStepComponent[]) => this.initWizardSteps(v));

    this.initWizardSteps(this.steps.toArray());
    merge(...this.steps.map((s) => s.change));

    merge(...this.steps.map((s) => s.change))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.resetConfirmClose();
        this.markForCheck();
      });

    this.steps.changes.pipe(untilDestroyed(this)).subscribe((steps: WizardStepComponent[]) => {
      this.cd.detectChanges();
      if (this.isOpenSteps) steps.forEach((step) => (step.completed = true));
    });

    this.setSanitizedHeight();

    if (this.firstStepTitle) this.setFirstStep(this.steps.toArray());

    this.markForCheck();
  }

  setSanitizedHeight(): void {
    if (this.height === this.defaultWizardHeight) {
      return void this.calculateDefaultHeight();
    }

    const height = typeof this.height === 'number' ? String(this.height) + 'px' : String(this.height);

    this.sanitizedHeight = this.sanitizer.bypassSecurityTrustStyle(height);
    delete this.sanitizedMinHeight;
    this.cd.detectChanges();
  }

  resetConfirmClose(): void {
    if (this.confirmClose != null && this.confirmClose) this.confirmClose = false;
  }

  private runCloseStrategy(force?: boolean, reason?: string): void {
    const final = () => {
      (this.closeHandler ? this.closeHandler() : of(null)).pipe(take(1), untilDestroyed(this)).subscribe(() => {
        this.closeModal(reason || discardChangesText);
      });
    };

    const existStepWithChanges = this.steps.some((s) => s.hasChanges);
    const shouldConfirm = !force && this.checkChanges && existStepWithChanges && !this.canCloseWithoutSave;

    if (shouldConfirm) {
      this.confirmClose = true;

      return void this.modalService
        .confirm(this.unsavedChangesSettings, 'All changes will be lost')
        .then((back: boolean) => (this.confirmClose = false))
        .catch(() => this.confirmClose && final());
    }

    final();
  }

  private canSaveWizard(): boolean {
    return (this.allStepValid && this.currentStep === this.steps.last) || this.showSaveBtnOnClose;
  }

  private initWizardSteps(newSteps: WizardStepComponent[]): void {
    if (!this.stepsOrientation) {
      this.stepsOrientation = newSteps.length > 5 ? 'vertical' : 'horizontal';
    }

    this.internalStep = this.internalStep || newSteps[0];
    this.internalStep.visible = true;
    this.internalStep.activated = true;

    this.notifyValueChange();
  }

  goToRequiredStep(skipCurrentStep = false): void {
    const steps = this.steps.toArray();
    const queue = this.stepQueue.getQueue();
    const nextStep = steps.find((step) => {
      return skipCurrentStep ? queue.includes(step.title) && step.title !== this.currentStep.title : queue.includes(step.title);
    });

    if (nextStep) this.currentStep = nextStep;
  }

  goToErrorStep(skipCurrentStep = false): void {
    const steps = this.steps.toArray();
    const idx = steps.findIndex((step) => (!step.valid || !step.completed) && (!skipCurrentStep || step.title !== this.currentStep.title));

    if (~idx) this.currentStep = steps[idx];
  }

  /**
   * Change step to N positions
   * @param {number} direction
   */
  changeStep(direction: number): void {
    this.currentStep.isNext = direction >= 0;
    this.currentStep.isSave = false;
    this.internalStep.changesBeforeValidation = this.internalStep.hasChanges;

    if (!this.currentStep.valid && direction > 0) {
      this.currentStep.nextEvent.emit(this.currentStep);

      return;
    }

    this.currentStep = this.getStepByDirection(direction) || this.currentStep;
  }

  nextButtonClicked() {
    this.currentStep.nextButtonClicked.emit();
    this.changeStep(1);
  }

  backButtonClicked() {
    this.currentStep.backButtonClicked.emit();
    this.changeStep(-1);
  }

  saveButtonClicked() {
    this.currentStep.saveButtonClicked.emit();
    this.handleSave();
  }

  /**
   * Open some step
   * @param {DOMEvent<HTMLElement>} event
   * @param {WizardStepComponent} value
   */
  selectManualStep(event: DOMEvent<HTMLElement>, value: WizardStepComponent): void {
    const stepsArray = this.steps.toArray();
    const currentStepIndex = stepsArray.findIndex((s) => s.title === this.currentStep.title);
    const targetStepIndex = stepsArray.findIndex((s) => s.title === value.title);

    this.currentStep.isNext = targetStepIndex > currentStepIndex;

    if (event?.target) event.target.blur();

    this.stepSelected.emit(value);
    this.currentStep = value;
  }

  /**
   * Set active and complete classes for previous and current step
   * @param {WizardStepComponent} value
   */
  updateStepState(value: WizardStepComponent): void {
    const steps = this.steps.toArray();

    if (steps.indexOf(value) > steps.indexOf(this.internalStep)) {
      this.internalStep.complete();
    } else {
      this.internalStep.notActivate();
    }

    value.activate();

    this.markForCheck();
  }

  notifyValueChange(): void {
    if (this.onChange) {
      setTimeout(() => this.onChange(this.currentStep), 100);
    }
  }

  markForCheck(): void {
    if (!(this.cd as ViewRef).destroyed) {
      this.cd.markForCheck();
    }
  }

  writeValue(obj: WizardStep): void {
    if (!obj) return;

    this.selectManualStep(null, this.steps.find((g) => g.title === obj.title) || this.steps.first);
  }

  registerOnChange(fn: (value) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Reset all follow steps after this step
   * @param {{title: string}} step Start step to reset
   */
  resetStepsAfter(step: WizardStep): void {
    const stepsArray = this.steps.toArray();
    const index = stepsArray.findIndex((s) => s.title === step.title);

    stepsArray.slice(index + 1).forEach((step) => step.reset());
    this.markForCheck();
  }

  stepContainerClasses(): string[] {
    const classesArr: string[] = [];

    if (this.stepsOrientation === 'vertical' && this.showSteps) classesArr.push('col-9');
    if (this.stepsOrientation === 'vertical' && !this.showSteps) classesArr.push('col-12');
    if (this.stepsContainerClass) classesArr.push(this.stepsContainerClass);

    return classesArr;
  }
  stepClasses(step: WizardStepComponent): any {
    const stepInQueue = this.stepQueue?.getQueue().includes(step.title);

    return {
      [this.activeClass]: step.activated,
      [this.invalidClass]: !step.valid,
      [this.completeClass]: (this.isOpenSteps && step.valid) || step.completed,
      [this.completeClass]: !stepInQueue && ((this.isOpenSteps && step.valid) || step.completed),
      disabled: !(step.completed || step.activated || step.canOpen) && !this.isOpenSteps
    };
  }

  ngOnInit(): void {
    const modal = this.document.querySelector<HTMLElement>('ngb-modal-window');

    if (modal) modal.focus();

    addBreadcrumb({
      category: 'wizard',
      timestamp: new Date().getTime() / 1000,
      message: `Open wizard '${this.title}'`,
      level: 'info' as SeverityLevel
    });
  }

  ngOnDestroy() {
    addBreadcrumb({
      category: 'wizard',
      timestamp: new Date().getTime() / 1000,
      message: `Close wizard '${this.title}'`,
      level: 'info' as SeverityLevel
    });
  }

  handleStepScroll(): void {
    this.stepScroll.emit();
  }

  private getStepByDirection(direction: number): WizardStepComponent {
    const s = this.steps.toArray();
    let index = direction;
    let step = s[s.indexOf(this.currentStep) + index];
    const isFirst = !this.currentStep.isNext && this.steps.first.title === step.title;
    const isLast = this.currentStep.isNext && this.steps.last.title === step.title;

    if (this.ignoreSteps.includes(step?.title) && !isLast && !isFirst) {
      step.complete();
      index = this.currentStep.isNext ? index + 1 : index - 1;
      step = s[s.indexOf(this.currentStep) + index];
    }

    return step;
  }

  private setFirstStep(steps: WizardStepComponent[]): void {
    const initialStep = steps.find((step) => step.title === this.firstStepTitle);

    initialStep && this.selectManualStep(null, initialStep);
  }

  private calculateDefaultHeight(): void {
    const header = this.header?.nativeElement.offsetHeight || 0;
    const footer = this.footer?.nativeElement.offsetHeight || 0;

    this.sanitizedHeight = this.defaultWizardHeight;
    this.sanitizedMinHeight = this.defaultWizardMinHeight;

    if (header || footer) {
      this.sanitizedHeight = `calc(${this.defaultWizardHeight} - ${header + footer}px)`;
      this.sanitizedMinHeight = `calc(${this.defaultWizardMinHeight} - ${header + footer}px)`;
    }

    this.cd.detectChanges();
  }

  private closeModal(reason: string): void {
    this.activeModal.close(reason);
    this.closeModalEvent.emit(reason);
  }
}
